import ErrorSummary from '@/components/ErrorSummary'
import { PhoneInputGroup } from '@/containers/forms/person/PhoneInputGroup'
import type { PoleasPublicBasicContacts } from '@/dtos'
import { useSelections } from '@/hooks'
import { useSaveBasicContacts } from '@/hooks/usePoleasPublic'
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  VStack
} from '@chakra-ui/react'
import { SelectionType } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { useState } from 'react'
import * as yup from 'yup'


export const schema: yup.ObjectSchema<PoleasPublicBasicContacts> = yup.object({
  mobilePhonePrefix: yup
    .string()
    .required('Vyberte předvolbu.'),

  mobilePhone: yup
    .string()
    .required('Zadejte Váš mobilní telefon.')
    .matches(/^\d{9}$/, 'Telefonní číslo musí mít přesně 9 číslic.'),

  phone: yup
    .string()
    .nullable()
    .transform((v) => (v === '' ? null : v))
    .matches(/^\d{9}$/, 'Telefonní číslo musí mít přesně 9 číslic.')
    .optional(),

  email: yup
    .string()
    .required('Zadejte Váš kontaktní email.')
    .email('Zadejte platný email.'),

  isContracted: yup
    .boolean()
    .optional(),

  clientDisabled: yup
    .boolean()
    .optional(),

  lastWizardStep: yup
    .number()
    .optional(),
})


export const BasicContactsForm = ({
  basicContacts,
  submitCallback,
}: {
  basicContacts: PoleasPublicBasicContacts
  submitCallback: () => void
}) => {

  const [submitting, setSubmitting] = useState(false)
  const { mutateAsync: saveBasicContacts } = useSaveBasicContacts()

  useSelections(SelectionType.CenaVozidlaZ)

  const methods = useForm<PoleasPublicBasicContacts>({
    mode: 'onBlur',
    defaultValues: basicContacts,
    resolver: yupResolver(schema),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = methods

  const submitForm = handleSubmit(async (formValues) => {
    try {
      setSubmitting(true)

      saveBasicContacts(formValues)
        .then(() => {
          submitCallback()
        })
        .catch((err) => {
          if (!(err.response && err.response.status === 400 && err.response.data))
            return

          const data = err.response.data

          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })
        })

    } finally {
      setSubmitting(false)
    }
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <VStack align="stretch" spacing={5}>

          <FormControl isInvalid={!!errors.mobilePhone} isRequired>
            <HStack spacing={2} mb={1}>
              <Flex alignItems={'center'} gap={2}>
                <FormLabel m="0">Váš mobilní telefon</FormLabel>
              </Flex>
            </HStack>

            <PhoneInputGroup
              select={{ ...register('mobilePhonePrefix') }}
              input={{ ...register('mobilePhone') }} />
          </FormControl>

          <FormControl isInvalid={!!errors.phone}>
            <HStack spacing={2} mb={1}>
              <Flex alignItems={'center'} gap={2}>
                <FormLabel m="0">Jiný kontaktní telefon</FormLabel>
              </Flex>
            </HStack>

            <Input {...register('phone')} />
          </FormControl>

          <FormControl isInvalid={!!errors.email} isRequired>
            <HStack spacing={2} mb={1}>
              <Flex alignItems={'center'} gap={2}>
                <FormLabel m="0"> Váš kontaktní email</FormLabel>
              </Flex>
            </HStack>

            <Input {...register('email')} />
          </FormControl>


          <Button
            type="submit"
            size="lg"
            bg={'red.500'}
            _hover={{ bg: '#c6261a' }}
            color="white"
            isLoading={submitting}
            isDisabled={submitting}
            loadingText="Ukládám…"
          >
            Pokračovat
          </Button>

          <ErrorSummary errors={errors} />
        </VStack>
      </form>
    </FormProvider>
  )
}
